import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { vendorsApi, type VendorInput } from '../features/vendors/api.ts';
import { vendorTypesApi } from '../features/vendorTypes/api.ts';
import { SendCredentials } from '../components/SendCredentials.tsx';
import { useAuthStore } from '../store/auth.ts';
import { canProcure } from '../lib/roles.ts';
import { t } from '../lib/strings.ts';
import { useConfirm } from '../components/ConfirmDialog.tsx';

type FieldErrors = Partial<Record<keyof VendorInput, string>>;

function parseServerError(err: unknown): FieldErrors {
  const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
  if (data?.error === 'invalid_id_card') {
    return { idCardNumber: t.vendorEdit.errorIdCard };
  }
  return {};
}

export function VendorEdit(): JSX.Element {
  const { vendorId } = useParams({ from: '/vendors/$vendorId' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const mayMutate = canProcure(role);
  const { confirm, dialog } = useConfirm();

  const { data: vendor, isLoading } = useQuery({
    queryKey: ['vendor', vendorId],
    queryFn: () => vendorsApi.get(vendorId),
  });

  const { data: types = [] } = useQuery({
    queryKey: ['vendor-types'],
    queryFn: () => vendorTypesApi.list(),
  });

  const [form, setForm] = useState<VendorInput | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivateSuccess, setDeactivateSuccess] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [activateSuccess, setActivateSuccess] = useState(false);

  // Per-vendor letterhead (mirrors OrgSettings header/footer/text controls)
  const [lhError, setLhError] = useState<string | null>(null);
  const [lhUploadingSide, setLhUploadingSide] = useState<'header' | 'footer' | null>(null);
  const [lhRemovingSide, setLhRemovingSide] = useState<'header' | 'footer' | null>(null);
  const lhHeaderInputRef = useRef<HTMLInputElement>(null);
  const lhFooterInputRef = useRef<HTMLInputElement>(null);

  // Populate form once vendor loads
  if (vendor && !form) {
    setForm({
      name: vendor.name,
      phone: vendor.phone ?? '',
      idCardNumber: vendor.idCardNumber,
      address: vendor.address ?? '',
      bankAccountNumber: vendor.bankAccountNumber ?? '',
      bankAccountName: vendor.bankAccountName ?? '',
      email: vendor.email ?? '',
      typeIds: vendor.types?.map((vt) => vt.id) ?? [],
    });
  }

  // Auto-save: the form persists on change (debounced). An init-once ref skips
  // the initial populate so a save's refetch can never clobber in-progress typing
  // (we also don't refetch the record being edited — only the list).
  const initialized = useRef(false);

  async function autoSave(next: VendorInput) {
    setFieldErrors({});
    setSubmitError(null);
    setSaveState('saving');
    try {
      await vendorsApi.update(vendorId, {
        name: next.name.trim(),
        phone: next.phone?.trim() || undefined,
        idCardNumber: next.idCardNumber.trim(),
        address: next.address?.trim() || undefined,
        bankAccountNumber: next.bankAccountNumber?.trim() || undefined,
        bankAccountName: next.bankAccountName?.trim() || undefined,
        email: next.email?.trim() || undefined,
        typeIds: next.typeIds,
      });
      void qc.invalidateQueries({ queryKey: ['vendors'] });
      setSaveState('saved');
    } catch (err) {
      const fe = parseServerError(err);
      if (Object.keys(fe).length > 0) {
        setFieldErrors(fe);
      } else {
        setSubmitError(t.vendorEdit.errorUpdate);
      }
      setSaveState('error');
    }
  }

  useEffect(() => {
    if (!form || !mayMutate) return;
    // Skip the first run (initial populate) — only user edits should save.
    if (!initialized.current) { initialized.current = true; return; }
    // Required fields must be present or the server rejects the patch.
    if (!form.name.trim() || !form.idCardNumber.trim()) return;
    const id = setTimeout(() => { void autoSave(form); }, 800);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);


  const handleUploadLhImage = async (side: 'header' | 'footer', file: File) => {
    setLhError(null);
    if (file.size > 2 * 1024 * 1024) {
      setLhError(t.vendorEdit.letterheadErrorFileSize);
      return;
    }
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setLhError(t.vendorEdit.letterheadErrorFileType);
      return;
    }
    setLhUploadingSide(side);
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve((reader.result as string).split(',')[1]!);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await vendorsApi.setLetterheadImage(vendorId, side, base64, file.type);
      await qc.invalidateQueries({ queryKey: ['vendor', vendorId] });
    } catch {
      setLhError(t.vendorEdit.letterheadErrorSave);
    } finally {
      setLhUploadingSide(null);
    }
  };

  const handleRemoveLhImage = async (side: 'header' | 'footer') => {
    setLhError(null);
    setLhRemovingSide(side);
    try {
      await vendorsApi.setLetterheadImage(vendorId, side, null, null);
      await qc.invalidateQueries({ queryKey: ['vendor', vendorId] });
    } catch {
      setLhError(t.vendorEdit.letterheadErrorSave);
    } finally {
      setLhRemovingSide(null);
    }
  };

  async function handleDeactivate() {
    if (!(await confirm({ body: t.vendorEdit.deactivateConfirm }))) return;
    setIsDeactivating(true);
    setSubmitError(null);
    try {
      await vendorsApi.deactivate(vendorId);
      void qc.invalidateQueries({ queryKey: ['vendors'] });
      void qc.invalidateQueries({ queryKey: ['vendor', vendorId] });
      setActivateSuccess(false);
      setDeactivateSuccess(true);
    } catch {
      setSubmitError(t.vendorEdit.errorDeactivate);
    } finally {
      setIsDeactivating(false);
    }
  }

  async function handleActivate() {
    setIsActivating(true);
    setSubmitError(null);
    try {
      await vendorsApi.activate(vendorId);
      void qc.invalidateQueries({ queryKey: ['vendors'] });
      void qc.invalidateQueries({ queryKey: ['vendor', vendorId] });
      setDeactivateSuccess(false);
      setActivateSuccess(true);
    } catch {
      setSubmitError(t.vendorEdit.errorActivate);
    } finally {
      setIsActivating(false);
    }
  }

  if (isLoading) {
    return (
      <div className="page">
        <p className="status-label text-muted-foreground" role="status">{t.vendorEdit.loading}</p>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="page">
        <p className="text-sm text-destructive">{t.vendorEdit.notFound}</p>
      </div>
    );
  }

  return (
    <div className="page space-y-6">
      {dialog}
      <header className="flex items-center gap-3">
        <button
          onClick={() => void navigate({ to: '/vendors' })}
          className="btn-ghost btn-sm"
          aria-label={t.vendorEdit.ariaBack}
        >
          <Icon name="arrow_forward" size={15} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-ink">{vendor.name}</h1>
          <p className="mono-meta num text-muted-foreground">{vendor.idCardNumber}</p>
        </div>
        <span className={['pill ms-auto', vendor.isActive ? 'pill-active' : 'pill-neutral'].join(' ')}>
          {vendor.isActive ? t.vendorEdit.statusActive : t.vendorEdit.statusInactive}
        </span>
      </header>

      {form && (
        <section className="card-paper space-y-4 p-5">
          <div className="flex items-center justify-between gap-3">
            <h2 className="status-label text-muted-foreground">{t.vendorEdit.editHeading}</h2>
            {mayMutate && saveState !== 'idle' && (
              <span
                className={['mono-meta', saveState === 'error' ? 'text-destructive' : 'text-muted-foreground'].join(' ')}
                role="status"
                aria-live="polite"
              >
                {saveState === 'saving' ? t.vendorEdit.saving : saveState === 'saved' ? t.vendorEdit.autosaveSaved : t.vendorEdit.errorUpdate}
              </span>
            )}
          </div>
          <div className="space-y-4">
            {/* Name */}
            <div className="space-y-1">
              <label className="field-label">{t.vendorEdit.fieldName} <span className="text-destructive">{t.vendorEdit.fieldRequired}</span></label>
              <input
                required
                className="input"
                value={form.name}
                onChange={(e) => setForm((f) => f ? { ...f, name: e.target.value } : f)}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {/* Phone */}
              <div className="space-y-1">
                <label className="field-label">{t.vendorEdit.fieldPhone}</label>
                <input
                  className="input num"
                  value={form.phone}
                  onChange={(e) => setForm((f) => f ? { ...f, phone: e.target.value } : f)}
                />
              </div>

              {/* ID Card */}
              <div className="space-y-1">
                <label className="field-label">
                  {t.vendorEdit.fieldIdCard} <span className="text-destructive">{t.vendorEdit.fieldRequired}</span>
                  <span className="ms-1 text-xs font-normal text-muted-foreground">{t.vendorEdit.fieldIdCardFormat}</span>
                </label>
                <input
                  required
                  className={['input num', fieldErrors.idCardNumber ? 'border-destructive' : ''].join(' ')}
                  placeholder={t.vendorEdit.fieldIdCardPlaceholder}
                  value={form.idCardNumber}
                  onChange={(e) => setForm((f) => f ? { ...f, idCardNumber: e.target.value } : f)}
                />
                {fieldErrors.idCardNumber && (
                  <p className="text-xs text-destructive" role="alert">{fieldErrors.idCardNumber}</p>
                )}
              </div>
            </div>

            {/* Address */}
            <div className="space-y-1">
              <label className="field-label">{t.vendorEdit.fieldAddress}</label>
              <input
                className="input"
                value={form.address}
                onChange={(e) => setForm((f) => f ? { ...f, address: e.target.value } : f)}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {/* Bank Account Number */}
              <div className="space-y-1">
                <label className="field-label">{t.vendorEdit.fieldBankAccountNumber}</label>
                <input
                  className="input num"
                  value={form.bankAccountNumber}
                  onChange={(e) => setForm((f) => f ? { ...f, bankAccountNumber: e.target.value } : f)}
                />
              </div>

              {/* Bank Account Name */}
              <div className="space-y-1">
                <label className="field-label">{t.vendorEdit.fieldBankAccountName}</label>
                <input
                  className="input"
                  value={form.bankAccountName}
                  onChange={(e) => setForm((f) => f ? { ...f, bankAccountName: e.target.value } : f)}
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-1">
              <label className="field-label">{t.vendorEdit.fieldEmail}</label>
              <input
                type="email"
                className="input"
                value={form.email}
                onChange={(e) => setForm((f) => f ? { ...f, email: e.target.value } : f)}
              />            </div>

            {/* Vendor Types */}
            {types.length > 0 && (
              <div className="space-y-1">
                <label className="field-label">{t.vendorEdit.fieldVendorTypes}</label>
                <div className="flex flex-wrap gap-2">
                  {types.map((vt) => (
                    <button
                      key={vt.id}
                      type="button"
                      onClick={() => setForm((f) => {
                        if (!f) return f;
                        const ids = f.typeIds ?? [];
                        return { ...f, typeIds: ids.includes(vt.id) ? ids.filter((id) => id !== vt.id) : [...ids, vt.id] };
                      })}
                      className={form.typeIds?.includes(vt.id) ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
                    >
                      {vt.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {submitError && (
              <p className="text-sm text-destructive" role="alert">{submitError}</p>
            )}
            {deactivateSuccess && (
              <p className="text-sm text-success" role="status">{t.vendorEdit.deactivateSuccess}</p>
            )}
            {activateSuccess && (
              <p className="text-sm text-success" role="status">{t.vendorEdit.activateSuccess}</p>
            )}

            {mayMutate && (
              <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
                {vendor.isActive ? (
                  <button
                    type="button"
                    onClick={() => void handleDeactivate()}
                    disabled={isDeactivating}
                    className="btn-danger btn-sm"
                  >
                    {isDeactivating ? t.vendorEdit.deactivating : t.vendorEdit.deactivate}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => void handleActivate()}
                    disabled={isActivating}
                    className="btn-secondary btn-sm"
                  >
                    {isActivating ? t.vendorEdit.activating : t.vendorEdit.activate}
                  </button>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Login credentials — procurement roles only */}
      {mayMutate && (
        <section className="card-paper p-5">
          <SendCredentials
            vendorId={vendorId}
            hasEmail={!!vendor.email}
            credentialsSentAt={vendor.credentialsSentAt}
          />
        </section>
      )}

      {/* Per-vendor letterhead — procurement roles only. Mirrors OrgSettings. */}
      {mayMutate && (
        <div className="space-y-3">
          <div>
            <h2 className="font-semibold text-ink">{t.vendorEdit.letterheadHeading}</h2>
            <p className="text-xs text-muted-foreground">{t.vendorEdit.letterheadHint}</p>
          </div>

          {lhError && <p className="text-sm text-destructive" role="alert">{lhError}</p>}

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Header image */}
            <section className="card-paper space-y-3 p-4">
              <h2 className="status-label text-muted-foreground">{t.vendorEdit.letterheadHeaderImageLabel}</h2>
              <p className="text-sm text-muted-foreground">
                {vendor.headerImagePath ? t.vendorEdit.currentImage : t.vendorEdit.noImage}
              </p>
              <input
                ref={lhHeaderInputRef}
                type="file"
                accept="image/png,image/jpeg"
                className="block text-sm"
                aria-label={t.vendorEdit.ariaUploadHeader}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleUploadLhImage('header', file);
                  e.target.value = '';
                }}
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => lhHeaderInputRef.current?.click()}
                  disabled={lhUploadingSide === 'header'}
                  className="btn-primary btn-sm"
                >
                  {lhUploadingSide === 'header' ? t.vendorEdit.letterheadSaving : t.vendorEdit.uploadBtn}
                </button>
                {vendor.headerImagePath && (
                  <button
                    type="button"
                    onClick={() => void handleRemoveLhImage('header')}
                    disabled={lhRemovingSide === 'header'}
                    className="btn-secondary btn-sm"
                  >
                    {lhRemovingSide === 'header' ? t.vendorEdit.removing : t.vendorEdit.removeImage}
                  </button>
                )}
              </div>
              <p className="text-xs text-muted-foreground"><span dir="ltr">1123 × 200 px</span> · {t.vendorEdit.uploadHint}</p>
            </section>

            {/* Footer image */}
            <section className="card-paper space-y-3 p-4">
              <h2 className="status-label text-muted-foreground">{t.vendorEdit.letterheadFooterImageLabel}</h2>
              <p className="text-sm text-muted-foreground">
                {vendor.footerImagePath ? t.vendorEdit.currentImage : t.vendorEdit.noImage}
              </p>
              <input
                ref={lhFooterInputRef}
                type="file"
                accept="image/png,image/jpeg"
                className="block text-sm"
                aria-label={t.vendorEdit.ariaUploadFooter}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleUploadLhImage('footer', file);
                  e.target.value = '';
                }}
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => lhFooterInputRef.current?.click()}
                  disabled={lhUploadingSide === 'footer'}
                  className="btn-primary btn-sm"
                >
                  {lhUploadingSide === 'footer' ? t.vendorEdit.letterheadSaving : t.vendorEdit.uploadBtn}
                </button>
                {vendor.footerImagePath && (
                  <button
                    type="button"
                    onClick={() => void handleRemoveLhImage('footer')}
                    disabled={lhRemovingSide === 'footer'}
                    className="btn-secondary btn-sm"
                  >
                    {lhRemovingSide === 'footer' ? t.vendorEdit.removing : t.vendorEdit.removeImage}
                  </button>
                )}
              </div>
              <p className="text-xs text-muted-foreground"><span dir="ltr">1123 × 100 px</span> · {t.vendorEdit.uploadHint}</p>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
